import {
  Editor,
  Plugin,
  IconCancel,
  IconCheck,
  IconEraser,
  ImageResize,
  ImageUtils,
  ButtonView,
  createLabeledInputText,
  InputTextView,
  LabeledFieldView,
  Locale,
} from "ckeditor5";

import "./SetImageSize.css";

import { IconResizeHorizontalLine } from "@examspark/common-ui/dist/components/icons";

import BalloonHelper, {
  BalloonHelperApi,
} from "../util/ContextualBalloonHelper";
import FocusableView from "../util/FocusableView";

export default class SetImageSize extends Plugin {
  static get requires() {
    return [ImageResize, ImageUtils];
  }

  private balloon: BalloonHelper;
  private imageUtils: ImageUtils;

  constructor(editor: Editor) {
    super(editor);

    this.balloon = BalloonHelper.register(editor, {
      createView: (api) => this.createView(api),
    });

    this.imageUtils = this.editor.plugins.get(ImageUtils);
  }

  init() {
    this.editor.ui.componentFactory.add("setImageSize", (locale) => {
      const button = new ButtonView(locale);
      button.set({
        label: this.editor.t("Set size"),
        icon: IconResizeHorizontalLine.Content,
        tooltip: true,
      });

      // command can't be null because we required the ImageResize plugin
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const command = this.editor.commands.get("resizeImage")!;

      button.bind("isEnabled").to(command, "isEnabled");
      button.on("execute", () => this.balloon.showView());

      return button;
    });
  }

  private createView(api: BalloonHelperApi) {
    const view = new ImageSizeView(this.editor.locale);
    view.on("save", () => {
      let width = view.width === "" ? null : (view.width ?? null);
      if (width?.match(/^\d+$/)) {
        width = width + "px";
      }
      this.editor.execute("resizeImage", { width });
      api.hideView();
    });
    view.on("cancel", () => api.hideView());

    api.on("show", () => {
      view.width = this.imageUtils
        .getClosestSelectedImageElement(this.editor.model.document.selection)
        ?.getAttribute("resizedWidth") as string | undefined;
      view.focus();
    });

    return view;
  }
}

class ImageSizeView extends FocusableView {
  declare public width: string | undefined;

  private fieldView: InputTextView;

  constructor(locale: Locale) {
    super(locale);

    // t can't be null because we passed a locale to super()
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const t = this.t!;

    this.set({
      width: undefined,
    });

    const widthFieldView = new LabeledFieldView(locale, createLabeledInputText);
    widthFieldView.set({ label: t("Width") });
    widthFieldView.fieldView.bind("value").to(this, "width");
    widthFieldView.fieldView.on("input", () => {
      this.width = widthFieldView.fieldView.element?.value;
    });

    const saveButton = new ButtonView(locale);
    saveButton.set({
      label: t("Save"),
      icon: IconCheck,
      class: "ck-button-save",
      tooltip: true,
    });

    const clearButton = new ButtonView(locale);
    clearButton.set({
      label: t("Clear"),
      icon: IconEraser,
      tooltip: true,
    });

    const cancelButton = new ButtonView(locale);
    cancelButton.set({
      label: t("Cancel"),
      icon: IconCancel,
      class: "ck-button-cancel",
      tooltip: true,
    });

    saveButton.on("execute", () => this.fire("save"));
    clearButton.on("execute", () => {
      this.width = "";
      this.fire("save");
    });
    cancelButton.on("execute", () => this.fire("cancel"));

    this.focusables.addMany([
      widthFieldView,
      clearButton,
      saveButton,
      cancelButton,
    ]);

    this.setTemplate({
      tag: "div",
      attributes: {
        class: ["ck", "ck-responsive-form", "rce-image-size-form"],
        tabindex: "-1",
      },
      children: this.focusables,
    });

    this.fieldView = widthFieldView.fieldView;

    this.on("closeRequest", () => this.fire("cancel"));
  }

  focus() {
    this.fieldView.select();
  }
}
